import { SpaCareReturn } from './SpaCareReturn';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PhoneIcon } from 'lucide-react';
import { business, routes } from '../data/site';
import { Product } from '../data/products';
import { shopCategories } from '../data/shopCategories';
import { SiteLink } from './SiteLink';
import { ProductCard } from './ProductCard';
export type ShopCategoryPageProps = {
  /** Document metadata */
  title: string;
  description: string;
  canonical: string;
  /** Header content */
  eyebrow: string;
  heading: string;
  intro: string;
  /** Optional helper block in the header's right column */
  helperText?: string;
  /** Toolbar */
  searchPlaceholder?: string;
  /** Catalog */
  products: Product[];
  /**
   * Optional custom presentation for the filtered/sorted results. Defaults to
   * the standard product-card grid.
   */
  renderProducts?: (products: Product[], addToCart: (quantity: number) => void) => ReactNode;
  /** Which shared category is current */
  currentHref: string;
  breadcrumbLabel: string;
  /** Content rendered between the grid and the closing CTA */
  children?: ReactNode;
  /** Closing CTA band */
  ctaEyebrow: string;
  ctaHeading: string;
  ctaCopy: string;
};
const sortOptions = [{
  value: 'featured',
  label: 'Featured'
}, {
  value: 'name',
  label: 'Name A–Z'
}, {
  value: 'price-asc',
  label: 'Price low to high'
}, {
  value: 'price-desc',
  label: 'Price high to low'
}];
const controlClass = 'mt-1.5 block w-full rounded-btn border border-ink/30 bg-white px-3 py-2.5 text-[16px] text-ink transition-colors duration-150 ease-out placeholder:text-ink/45 focus:border-pegasus-blue focus:outline-none focus:ring-2 focus:ring-pegasus-blue/30';
function upsertMeta(name: string, content: string) {
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute('name', name);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}
export function ShopCategoryPage({
  title,
  description,
  canonical,
  eyebrow,
  heading,
  intro,
  helperText,
  searchPlaceholder = 'Name or SKU',
  products,
  renderProducts,
  currentHref,
  breadcrumbLabel,
  children,
  ctaEyebrow,
  ctaHeading,
  ctaCopy
}: ShopCategoryPageProps) {
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('featured');
  const [cartCount, setCartCount] = useState(0);
  const navigate = useNavigate();
  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = title;
    upsertMeta('description', description);
    let link = document.querySelector<HTMLLinkElement>('link[rel="canonical"]');
    if (!link) {
      link = document.createElement('link');
      link.setAttribute('rel', 'canonical');
      document.head.appendChild(link);
    }
    link.setAttribute('href', canonical);
  }, [title, description, canonical]);
  const visibleProducts = useMemo(() => {
    const term = query.trim().toLowerCase();
    const filtered = term ? products.filter(product => product.name.toLowerCase().includes(term) || product.sku.includes(term)) : products;
    const sorted = [...filtered];
    if (sort === 'name') sorted.sort((a, b) => a.name.localeCompare(b.name));
    if (sort === 'price-asc') sorted.sort((a, b) => a.price - b.price);
    if (sort === 'price-desc') sorted.sort((a, b) => b.price - a.price);
    return sorted;
  }, [products, query, sort]);
  return <main data-id="element-396" data-mp="3up88nra79f33">
      {currentHref !== routes.bigGreenEggAccessories && <SpaCareReturn data-id="element-397" data-mp="3px6zie8bw4ha" />}
      {/* Breadcrumb + category header */}
      <section aria-labelledby="category-h1" className="w-full bg-water" data-id="element-398" data-mp="3pvm1gemt23eo">
        <div className="mx-auto max-w-container px-5 py-8 sm:px-8 lg:py-10" data-id="element-399" data-mp="3kxknu985ewal">
          <nav aria-label="Breadcrumb" data-id="element-400" data-mp="m8n5ws85vi8f">
            <ol className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[15px] text-ink/70" data-id="element-401" data-mp="m9ok35kh4kud">
              <li data-id="element-402" data-mp="hgc8bw0w88j1">
                <SiteLink to={routes.home} className="underline decoration-ink/30 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-blue" data-id="element-403" data-mp="hftzlzx1o1u8">
                  Home
                </SiteLink>
              </li>
              <li aria-hidden="true" data-id="element-404" data-mp="3o8ydvuwax6es">/</li>
              <li data-id="element-405" data-mp="3t5f2srbbrosx">
                <SiteLink to={routes.supplies} className="underline decoration-ink/30 underline-offset-4 transition-colors duration-150 ease-out hover:text-pegasus-blue" data-id="element-406" data-mp="3t6zxcgxn37qs">
                  Shop Online
                </SiteLink>
              </li>
              <li aria-hidden="true" data-id="element-407" data-mp="azac486pv7wj">/</li>
              <li aria-current="page" className="font-medium text-ink" data-id="element-408" data-mp="ayhfi01h1ys4">
                {breadcrumbLabel}
              </li>
            </ol>
          </nav>

          <div className="mt-5 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12" data-id="element-409" data-mp="13o7k9ih7robk">
            <div className={helperText ? 'lg:col-span-7' : 'lg:col-span-9'} data-id="element-410" data-mp="18fzcdxg0q23t">
              <p className="text-[14px] font-bold uppercase text-pegasus-blue" data-id="element-411" data-mp="2t5v2qvarodwq">{eyebrow}</p>
              <h1 id="category-h1" className="mt-2 text-h2-mobile font-semibold text-ink lg:text-[40px] lg:leading-[48px]" data-id="element-412" data-mp="2o71lqsw9u4rt">
                {heading}
              </h1>
              <p className="mt-4 text-body text-ink/80 sm:text-body-lg" data-id="element-413" data-mp="2xwbfseaodxik">{intro}</p>
            </div>

            <div className={helperText ? 'lg:col-span-5' : 'hidden'} data-id="element-414" data-mp="2a6quvpuyoq6n">
              <div className="border-t border-ink/20 pt-4" data-id="element-415" data-mp="2a93iojnehurm">
                <p className="text-[16px] leading-6 text-ink/85" data-id="element-416" data-mp="259qnnbmdlhdy">{helperText}</p>
                <div className="mt-4 flex flex-wrap items-center gap-3" data-id="element-417" data-mp="2j903292rc7gv">
                  <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-blue px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-ink" data-id="element-418" data-mp="2e93xw6ibq9zq">
                    <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-419" data-mp="2nauznkjv9qmq" />
                    Call or Text
                  </a>
                  <SiteLink to={routes.spaCare} className="inline-flex min-h-[48px] items-center whitespace-nowrap rounded-btn border border-ink/25 bg-white px-5 text-[16px] font-bold text-ink transition-colors duration-150 ease-out hover:border-pegasus-blue" data-id="element-420" data-mp="2s8mw8rvgi0x9">
                    Spa Care &amp; FAQs
                  </SiteLink>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Toolbar */}
      <section aria-labelledby="toolbar-heading" className="w-full border-y border-ink/10 bg-white" data-id="element-421" data-mp="2dk06dw87dqrs">
        <h2 id="toolbar-heading" className="sr-only" data-id="element-422" data-mp="2id30q6kt1491">
          Catalog tools
        </h2>
        <div className="mx-auto max-w-container px-5 py-5 sm:px-8" data-id="element-423" data-mp="1kvcqyha4xvm5">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2" data-id="element-424" data-mp="1pup9iptz4gix">
            <div data-id="element-425" data-mp="1psunt9avokds">
              <label htmlFor="category-search" className="block text-[15px] font-semibold text-ink" data-id="element-426" data-mp="1ibg232512rwi">
                Search products
              </label>
              <input id="category-search" type="search" value={query} onChange={event => setQuery(event.target.value)} placeholder={searchPlaceholder} className={controlClass} data-id="element-427" data-mp="1dd4uky27gthg" />
            </div>
            <div data-id="element-428" data-mp="ze53ljr802i4">
              <label htmlFor="category-sort" className="block text-[15px] font-semibold text-ink" data-id="element-429" data-mp="zenadabidz4a">
                Sort by
              </label>
              <select id="category-sort" value={sort} onChange={event => setSort(event.target.value)} className={controlClass} data-id="element-430" data-mp="po2dbrbhh2nh">
                {sortOptions.map(option => <option key={option.value} value={option.value} data-id="element-431" data-mp="bn9eiue5flek">
                    {option.label}
                  </option>)}
              </select>
            </div>
          </div>

          <div className="mt-4 flex flex-wrap items-center justify-between gap-x-6 gap-y-1" data-id="element-432" data-mp="24j0k9vx9ub1i">
            <p className="text-[15.5px] text-ink/70" data-id="element-433" data-mp="1qj9daf569ux0">
              {visibleProducts.length} product{visibleProducts.length === 1 ? '' : 's'}
            </p>
            <p aria-live="polite" className="text-[15.5px] text-ink/70" data-id="element-434" data-mp="1ujjllygfsxf1">
              {cartCount > 0 ? `Cart: ${cartCount} item${cartCount === 1 ? '' : 's'} (preview only)` : ''}
            </p>
          </div>
        </div>
      </section>

      {/* Catalog */}
      <section aria-labelledby="catalog-heading" className="w-full bg-white" data-id="element-435" data-mp="16wl36v8rbfeh">
        <h2 id="catalog-heading" className="sr-only" data-id="element-436" data-mp="1btlcrg4tf0ac">
          {breadcrumbLabel} products
        </h2>
        <div className="mx-auto max-w-container px-5 py-10 sm:px-8 lg:py-12" data-id="element-437" data-mp="xubxqjscic7y">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-10" data-id="element-438" data-mp="12teyvt57fyan">
            {/* Category navigation */}
            <div className="lg:col-span-3" data-id="element-439" data-mp="j73hh4k6jgp4">
              {/* Tablet and mobile control */}
              <div className="lg:hidden" data-id="element-440" data-mp="e9lfxz8hs3r5">
                <label htmlFor="category-jump" className="block text-[15px] font-semibold text-ink" data-id="element-441" data-mp="eamq81hoom7t">
                  Category
                </label>
                <select id="category-jump" value={currentHref} onChange={event => navigate(event.target.value)} className={controlClass} data-id="element-442" data-mp="o92ii7sg61iw">
                  {shopCategories.map(category => <option key={category.href} value={category.href} data-id="element-443" data-mp="1bw0t4otvyf8w">
                      {category.label}
                    </option>)}
                </select>
              </div>

              {/* Desktop list */}
              <nav aria-label="Shop categories" className="hidden lg:block" data-id="element-444" data-mp="1fy5kti0rx1sw">
                <p className="text-[13px] font-bold uppercase text-ink/60" data-id="element-445" data-mp="1fw2qilsl8mid">Categories</p>
                <ul className="mt-3 border-t border-ink/15" data-id="element-446" data-mp="329ulk0lygte7">
                  {shopCategories.map(category => {
                  const isCurrent = category.href === currentHref;
                  return <li key={category.href} className="border-b border-ink/10" data-id="element-447" data-mp="2sgdpepycj27d">
                        <SiteLink to={category.href} aria-current={isCurrent ? 'page' : undefined} className={`block py-2.5 text-[16px] leading-6 transition-colors duration-150 ease-out ${isCurrent ? 'font-bold text-ink' : 'text-pegasus-blue hover:text-pegasus-magenta'}`} data-id="element-448" data-mp="2nlggrk4zauv0">
                          {category.label}
                        </SiteLink>
                      </li>;
                })}
                </ul>
              </nav>
            </div>

            {/* Product grid */}
            <div className="lg:col-span-9" data-id="element-449" data-mp="fc0cawurxkbm">
              {visibleProducts.length > 0 ? renderProducts ? renderProducts(visibleProducts, quantity => setCartCount(count => count + quantity)) : <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3" data-id="element-450" data-mp="u0wrrez5tzm9">
                    {visibleProducts.map(product => <ProductCard key={product.sku} product={product} onAddToCart={quantity => setCartCount(count => count + quantity)} data-id="element-451" data-mp="y16umirno9ww" />)}
                  </div> : <p className="text-body text-ink/75" data-id="element-452" data-mp="34usidbcs83hc">
                  No products in this category match that search. Try a different term, or call or
                  text {business.phone} for help finding a product.
                </p>}
            </div>
          </div>
        </div>
      </section>

      {children}

      {/* Closing CTA */}
      <section aria-labelledby="category-cta-heading" className="w-full bg-ink" data-id="element-453" data-mp="3bpsq1gkbyiy3">
        <div className="mx-auto max-w-container px-5 py-12 sm:px-8 lg:py-14" data-id="element-454" data-mp="36pwn7kndcxk4">
          <div className="max-w-3xl" data-id="element-455" data-mp="3kq7f8r0f0i6v">
            <p className="text-[14px] font-bold uppercase text-white/70" data-id="element-456" data-mp="3kp5qxkue1z77">{ctaEyebrow}</p>
            <h2 id="category-cta-heading" className="mt-2 text-h2-mobile font-semibold text-white lg:text-h2-desktop" data-id="element-457" data-mp="3gpf57vjpy99j">
              {ctaHeading}
            </h2>
            <p className="mt-4 text-body text-white/85 sm:text-body-lg" data-id="element-458" data-mp="3pgntgdc287ua">{ctaCopy}</p>
          </div>

          <div className="mt-7 flex flex-wrap items-center gap-3" data-id="element-459" data-mp="880w3wmmvox9">
            <a href={business.phoneHref} className="inline-flex min-h-[48px] items-center gap-2 whitespace-nowrap rounded-btn bg-pegasus-magenta px-6 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-[#bb2f83]" data-id="element-460" data-mp="39plvksffcei">
              <PhoneIcon className="h-4 w-4" aria-hidden="true" data-id="element-461" data-mp="qurvq36z2ngt" />
              Call or Text {business.phone}
            </a>
            <SiteLink to={routes.location} className="inline-flex min-h-[48px] items-center whitespace-nowrap rounded-btn border-2 border-white/70 px-5 text-[16px] font-bold text-white transition-colors duration-150 ease-out hover:bg-white hover:text-ink" data-id="element-462" data-mp="hw66xqawqc7l">
              Visit the Showroom
            </SiteLink>
          </div>
        </div>
      </section>
    </main>;
}